import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Search, TrendingUp, Package, Building2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PurchaseHistory = () => {
  const [records, setRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchRecords = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/purchase-records`);
      setRecords(data);
    } catch (e) {
      console.error('Error fetching purchase records:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const filteredRecords = useMemo(() => {
    if (!searchTerm) return records;
    const lower = searchTerm.toLowerCase();
    return records.filter(
      (r) =>
        (r.vendorName || '').toLowerCase().includes(lower) ||
        (r.billReference || '').toLowerCase().includes(lower)
    );
  }, [searchTerm, records]);

  const stats = useMemo(() => {
    const totalSpent = records.reduce((sum, r) => {
      return sum + (r.items || []).reduce((s, i) => s + (i.quantity * i.mrp), 0);
    }, 0);
    const uniqueVendors = new Set(records.map((r) => r.vendorName).filter(Boolean)).size;
    const totalItems = records.reduce((sum, r) => sum + (r.totalItems || 0), 0);
    return { totalSpent, uniqueVendors, totalItems };
  }, [records]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const renderEmptyMessage = () => {
    if (records.length === 0) {
      return 'No purchase records yet. Use AI Features → Purchase Bill Scanner to add one.';
    }
    return 'No records match your search.';
  };

  const renderTableBody = () => {
    if (loading) {
      return <div className="text-center py-12" style={{ color: '#6B7280' }}>Loading purchase records...</div>;
    }
    if (filteredRecords.length === 0) {
      return (
        <div className="text-center py-12" data-testid="no-purchase-records">
          <Package size={48} style={{ color: '#6B7280', margin: '0 auto' }} />
          <p className="mt-4" style={{ color: '#6B7280' }}>{renderEmptyMessage()}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-4xl font-bold" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }} data-testid="purchase-history-title">
          Purchase History
        </h1>
        <p className="mt-2" style={{ color: '#6B7280' }}>Review past vendor bills and spending trends</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="card p-6" data-testid="stat-total-spent">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide font-medium" style={{ color: '#78716C' }}>Total Spent</p>
              <p className="text-3xl font-bold mt-2" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
                ₹{stats.totalSpent.toFixed(2)}
              </p>
            </div>
            <div className="p-4 rounded-xl" style={{ backgroundColor: '#F5E6F5' }}>
              <TrendingUp size={28} style={{ color: '#990099' }} />
            </div>
          </div>
        </div>

        <div className="card p-6" data-testid="stat-vendors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide font-medium" style={{ color: '#78716C' }}>Unique Vendors</p>
              <p className="text-3xl font-bold mt-2" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
                {stats.uniqueVendors}
              </p>
            </div>
            <div className="p-4 rounded-xl" style={{ backgroundColor: '#F5E6F5' }}>
              <Building2 size={28} style={{ color: '#990099' }} />
            </div>
          </div>
        </div>

        <div className="card p-6" data-testid="stat-items">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide font-medium" style={{ color: '#78716C' }}>Items Purchased</p>
              <p className="text-3xl font-bold mt-2" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
                {stats.totalItems}
              </p>
            </div>
            <div className="p-4 rounded-xl" style={{ backgroundColor: '#F5E6F5' }}>
              <Package size={28} style={{ color: '#990099' }} />
            </div>
          </div>
        </div>
      </div>

      <div className="card p-6">
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2" size={20} style={{ color: '#6B7280' }} />
            <input
              type="text"
              placeholder="Search by vendor name or bill reference..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: '2.5rem' }}
              data-testid="purchase-search-input"
            />
          </div>
        </div>

        {(loading || filteredRecords.length === 0) ? (
          renderTableBody()
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="purchase-records-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Vendor</th>
                  <th>Bill Reference</th>
                  <th>Items</th>
                  <th>Total Amount</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map((record) => {
                  const total = (record.items || []).reduce((s, i) => s + (i.quantity * i.mrp), 0);
                  return (
                    <React.Fragment key={record.id}>
                      <tr data-testid={`purchase-row-${record.id}`}>
                        <td>{formatDate(record.date)}</td>
                        <td className="font-medium" style={{ color: '#2D3A2A' }}>
                          {record.vendorName || <span style={{ color: '#6B7280' }}>—</span>}
                        </td>
                        <td>{record.billReference || <span style={{ color: '#6B7280' }}>—</span>}</td>
                        <td>{record.totalItems}</td>
                        <td className="font-bold" style={{ color: '#990099' }}>₹{total.toFixed(2)}</td>
                        <td>
                          <button
                            onClick={() => setExpandedId(expandedId === record.id ? null : record.id)}
                            className="secondary-btn text-sm"
                            data-testid={`toggle-details-${record.id}`}
                          >
                            {expandedId === record.id ? 'Hide' : 'View'} Items
                          </button>
                        </td>
                      </tr>
                      {expandedId === record.id && (
                        <tr data-testid={`purchase-details-${record.id}`}>
                          <td colSpan={6} style={{ backgroundColor: '#F9F6F0', padding: '1rem 2rem' }}>
                            <p className="text-xs uppercase tracking-wide font-medium mb-3" style={{ color: '#78716C' }}>
                              Items in this purchase
                            </p>
                            <div className="bg-white rounded-lg overflow-hidden">
                              <table>
                                <thead>
                                  <tr>
                                    <th>Medicine</th>
                                    <th>Quantity</th>
                                    <th>MRP / unit</th>
                                    <th>Line Total</th>
                                    <th>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {record.items.map((item, i) => (
                                    <tr key={i}>
                                      <td className="font-medium" style={{ color: '#2D3A2A' }}>{item.name}</td>
                                      <td>{item.quantity}</td>
                                      <td>₹{item.mrp.toFixed(2)}</td>
                                      <td className="font-medium" style={{ color: '#990099' }}>₹{(item.quantity * item.mrp).toFixed(2)}</td>
                                      <td>
                                        <span
                                          className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
                                          style={{
                                            backgroundColor: item.isNew ? '#DBEAFE' : '#D1FAE5',
                                            color: item.isNew ? '#1E40AF' : '#065F46',
                                          }}
                                        >
                                          {item.isNew ? 'New Added' : 'Updated'}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchaseHistory;
